import * as React from "react";
import { Parser, ImportResult } from "./types";
import { parseContent, defaultParsers } from "./parsers";
import { formSetValue, formTriggerValidation } from "../../pages/gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/notificationDrawerSlice";

export interface UseImportOptions<TExisting, TResult> {
  formId: string;
  fieldName: string;
  /** Label for the action shown in notifications (e.g., "Import dictionary secret") */
  actionLabel?: string;
  parsers?: Parser[];
  transform: (parsed: Record<string, unknown>, existing: TExisting) => ImportResult<TResult>;
  onSuccess?: (result: ImportResult<TResult>) => void;
  onError?: (error: string) => void;
}

export function useImport<TExisting, TResult>(
  options: UseImportOptions<TExisting, TResult>,
  existingValue: TExisting
) {
  const { formId, fieldName, actionLabel = "Import", parsers = defaultParsers, transform, onSuccess, onError } = options;
  const dispatch = useAppDispatch();
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  const importContent = React.useCallback(
    (content: string, fileName?: string) => {
      const contentPreview = content.length > 500 ? content.slice(0, 500) + "..." : content;

      // 1. Parse the content
      const parseResult = parseContent(content, parsers);

      if (!parseResult.success || !parseResult.data) {
        const errorMsg = parseResult.error || "Could not parse file";
        onError?.(errorMsg);
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: `Failed to parse file: ${errorMsg}`,
            details: {
              error: errorMsg,
              fileName: fileName || "unknown",
              contentPreview,
              attemptedFormats: parsers.map((p) => p.name),
            },
          })
        );
        return;
      }

      // 2. Transform parsed data
      const transformResult = transform(parseResult.data, existingValue);

      if (!transformResult.success || transformResult.data === null) {
        const errorMsg = transformResult.error || "Failed to transform data";
        onError?.(errorMsg);
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: `Failed to transform data: ${errorMsg}`,
            details: {
              error: errorMsg,
              fileName: fileName || "unknown",
              parsedFormat: parseResult.format,
              parsedKeys: Object.keys(parseResult.data),
              parsedData: parseResult.data,
            },
          })
        );
        return;
      }

      // 3. Update form value
      formSetValue(formId, fieldName, transformResult.data);

      // 4. Trigger validation
      formTriggerValidation(formId);

      // 5. Determine result count
      const itemCount = Array.isArray(transformResult.data) ? transformResult.data.length : 1;

      // 6. Handle different success scenarios
      if (itemCount === 0) {
        // Empty result - partial success
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: "File was parsed but contained no valid data",
            details: {
              fileName: fileName || "unknown",
              parsedFormat: parseResult.format,
              parsedKeys: Object.keys(parseResult.data),
              hint: "Check if the file format matches expected key-value structure",
            },
          })
        );
        onError?.("File was imported but the result is empty");
        return;
      }

      const hasLossyKeys = transformResult.lossyKeys && transformResult.lossyKeys.length > 0;
      const hasOverrides = transformResult.overriddenKeys.length > 0;

      if (hasLossyKeys) {
        // Partial success - some data was lost during conversion
        const lossyCount = transformResult.lossyKeys!.length;
        const messageParts: string[] = [`Imported ${itemCount} item${itemCount !== 1 ? "s" : ""}`];

        if (hasOverrides) {
          messageParts.push(`${transformResult.overriddenKeys.length} overridden`);
        }
        messageParts.push(`${lossyCount} with data loss`);

        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: messageParts.join(", "),
            details: {
              warning: "Some values contained nested structures that were converted to strings",
              lossyKeys: transformResult.lossyKeys!.map((lk) => ({
                key: lk.key,
                reason: lk.reason === "nested_object"
                  ? "Nested object flattened to JSON string"
                  : lk.reason === "array"
                  ? "Array converted to comma-separated string"
                  : lk.reason === "null"
                  ? "Null converted to empty string"
                  : "Undefined converted to empty string",
                originalType: lk.originalType,
              })),
              overriddenKeys: hasOverrides ? transformResult.overriddenKeys : undefined,
            },
          })
        );
      } else if (hasOverrides) {
        // Success with overrides
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: true,
            message: `Imported ${itemCount} item${itemCount !== 1 ? "s" : ""}, ${transformResult.overriddenKeys.length} key${transformResult.overriddenKeys.length !== 1 ? "s" : ""} overridden`,
          })
        );
      } else {
        // Full success
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: true,
            message: `Successfully imported ${itemCount} item${itemCount !== 1 ? "s" : ""}`,
          })
        );
      }

      // 7. Call success callback
      onSuccess?.(transformResult);
    },
    [formId, fieldName, actionLabel, parsers, transform, existingValue, onSuccess, onError, dispatch, page]
  );

  return { importContent };
}
